import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import { 
  isCaptchaEnabled, 
  shouldShowCaptchaForLogin, 
  getCaptchaSiteKey, 
  getCaptchaDisplaySettings,
  getCaptchaErrorMessage 
} from '../config/captcha';

const Login = () => {
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [captchaValue, setCaptchaValue] = useState(null);
  const [showCaptcha, setShowCaptcha] = useState(false);
  
  const { loginWithGoogle, currentUser } = useAuth();
  const navigate = useNavigate();
  const captchaRef = useRef();

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Redirect if user is already logged in
  useEffect(() => {
    if (currentUser) {
      navigate('/blog');
    }
  }, [currentUser, navigate]);

  // Check if CAPTCHA should be shown
  useEffect(() => {
    setShowCaptcha(shouldShowCaptchaForLogin(failedAttempts));
  }, [failedAttempts]);

  // Reset failed attempts after timeout
  useEffect(() => {
    if (failedAttempts > 0) {
      const timer = setTimeout(() => {
        setFailedAttempts(0);
      }, 300000); // 5 minutes

      return () => clearTimeout(timer);
    }
  }, [failedAttempts]);

  const handleCaptchaChange = (value) => {
    setCaptchaValue(value);
    if (error && error.includes('CAPTCHA')) {
      setError('');
    }
  };

  const handleCaptchaExpired = () => {
    setCaptchaValue(null);
    setError(getCaptchaErrorMessage('captchaExpired'));
  };

  const handleGoogleLogin = async () => {
    // Check CAPTCHA if required
    if (showCaptcha && isCaptchaEnabled() && !captchaValue) {
      setError(getCaptchaErrorMessage('captchaRequired'));
      return;
    }

    try {
      setError('');
      setLoading(true);
      
      await loginWithGoogle();
      
      // Reset failed attempts on successful login
      setFailedAttempts(0);
      
      // Navigation will happen automatically via useEffect when currentUser updates
    } catch (error) {
      setError(error.message);
      
      // Increment failed attempts
      setFailedAttempts(prev => prev + 1);
      
      // Reset CAPTCHA
      if (captchaRef.current) {
        captchaRef.current.reset();
      }
      setCaptchaValue(null);
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-brown-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Sign in to your account
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Access your account using Google authentication
          </p>
        </div>
        
        <div className="mt-8 space-y-6">
          {error && (
            <div className="text-red-600 text-sm text-center bg-red-50 p-3 rounded-md">
              {error}
            </div>
          )}

          {/* CAPTCHA Section */}
          {showCaptcha && isCaptchaEnabled() && (
            <div className="flex justify-center">
              <ReCAPTCHA
                ref={captchaRef}
                sitekey={getCaptchaSiteKey()}
                onChange={handleCaptchaChange}
                onExpired={handleCaptchaExpired}
                theme={getCaptchaDisplaySettings().theme}
                size={getCaptchaDisplaySettings().size}
              />
            </div>
          )}

          {failedAttempts > 0 && (
            <div className="text-amber-600 text-sm text-center bg-amber-50 p-3 rounded-md">
              <span className="font-medium">Security Notice:</span> {failedAttempts} failed attempt{failedAttempts > 1 ? 's' : ''} detected.
              {failedAttempts >= 3 && ' CAPTCHA verification is now required.'}
            </div>
          )}

          <div>
            <button
              onClick={handleGoogleLogin}
              disabled={loading}
              className="group relative w-full flex justify-center items-center py-3 px-4 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 transition-colors duration-200"
            >
              <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              {loading ? 'Signing in...' : 'Continue with Google'}
            </button>
          </div>

          <div className="text-center">
            <Link
              to="/"
              className="text-sm text-green-600 hover:text-green-500"
            >
              ← Back to Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
